/**
 * @file View state for "Manage targets & rules" (SE-7009), as Apollo reactive variables.
 * @description What the reader has CONFIGURED for the Continental and Domestic
 * rule layers. A store of its own rather than more keys on
 * `complianceViewStore`, for the same reason the PSR breakdown has one: this is
 * the state of ONE modal surface, and a reader of the page-level store should
 * not have to decide whether a rule threshold is page state.
 *
 * `makeVar` because that is what this repo uses for feature state — no Redux,
 * no Zustand.
 *
 * ## Why there is still no mutation behind the RULE THRESHOLDS
 *
 * The FC GraphQL schema exposes **no mutation that writes a rule threshold
 * for the Continental or Domestic layer**, for any rule other than Squad Cost
 * Ratio (which saves through `updateRegimeRuleTarget` instead —
 * `adapters/complianceSource.ts`'s `useUpdateRegimeRuleTarget`) —
 * `createClubObjective`/`updateClubObjective` are Internal-layer only and
 * belong to a different surface (SE-7004). So a threshold save for every
 * OTHER rule lands here and the surfaces re-read it, exactly as SE-7010's
 * target overrides and SE-7011's per-year figures do. When the configuration
 * write lands for the rest of the table (FC-4.1 / SE-7013) the server becomes
 * the truth and this part of the store goes with the fixtures — which is why
 * the rule table and the draft logic live in `domain/targetsRules*` rather
 * than in here.
 *
 * `adjustedRevenueVar` BELOW is the one exception: SE-7013 built the read
 * (`ComplianceQuantity.AdjustedRevenue` is a `complianceFinancialFigures`-
 * addressable quantity, not a read-only enum member as this file used to
 * claim) and SE-7593 built the write (`recordComplianceFinancialFigure`).
 * SE-8101 wires both dialogs' saves to it
 * (`adapters/adjustedRevenueSource.ts`) — see that variable's own doc for
 * what the reactive var still exists FOR now that a real mutation does.
 *
 * The rule-threshold half of this store is deliberately NOT written to
 * storage: a threshold that survived a reload would be indistinguishable
 * from a filed one.
 * @module features/financial-compliance/stores/targetsRulesStore
 */
import { makeVar } from "@apollo/client";

/**
 * Bumped by every {@link resetTargetsRulesState}; see {@link readTargetsRulesEpoch}
 * for what reads it and why a clear on its own leaves a window open.
 *
 * A `makeVar` rather than a module counter because `functional/no-let` bans the
 * latter, and this needs no subscription either way: callers invoke it directly
 * to read, which does not subscribe anything — only `useReactiveVar` does.
 */
const targetsRulesEpochVar = makeVar(0);

/**
 * Thresholds a save has committed, keyed by rule id then by field id.
 *
 * Sparse on purpose: a rule absent from the map keeps the value the ratified
 * frame seeds, so nothing has to be seeded here for rules nobody has touched.
 * Values are TEXT rather than numbers because that is how this feature carries
 * figures end to end — `domain/kpiTargets.parseKpiNumber` is the one place that
 * reads a number back out, and it is what `readTargetsRuleNumber` delegates to
 * so a negative loss limit ("-105") is read the same way everywhere.
 */
export const ruleThresholdOverridesVar = makeVar<
  Readonly<Record<string, Readonly<Record<string, string>>>>
>({});

/**
 * Records the thresholds a save committed for one rule.
 * @param ruleId - The rule that was edited.
 * @param values - Every field's text, keyed by field id.
 * @returns Nothing.
 */
export const setRuleThreshold = (
  ruleId: string,
  values: Readonly<Record<string, string>>
): void => {
  ruleThresholdOverridesVar({
    ...ruleThresholdOverridesVar(),
    [ruleId]: values,
  });
};

/**
 * Per-rule "Show KPI" positions, keyed by rule id.
 *
 * Sparse for the same reason: a rule absent from here is showing, because the
 * ratified dashboard ships with these cards placed.
 *
 * This is the record of what the READER asked for, per rule. It is not the
 * mechanism that removes a card — that is `complianceViewStore`'s
 * `setKpiOnDashboard` / `removedKpiIdsVar`, which the dialog also calls for the
 * rules that map to a card (SE-7009 AC2). Two mechanisms would be two answers
 * to "is this card placed"; this variable exists so a rule that places NO card
 * still remembers its switch.
 */
export const ruleShowKpiOverridesVar = makeVar<
  Readonly<Record<string, boolean>>
>({});

/**
 * Records one rule's Show KPI position without disturbing the others.
 * @param ruleId - The rule that was flipped.
 * @param isShown - Whether its KPI card should be placed.
 * @returns Nothing.
 */
export const setRuleShowKpi = (ruleId: string, isShown: boolean): void => {
  ruleShowKpiOverridesVar({ ...ruleShowKpiOverridesVar(), [ruleId]: isShown });
};

/**
 * The UEFA-adjusted revenue figure finance has entered, as text, or null.
 *
 * ONE FIELD, TWO SURFACES (SE-7009 AC3). The Manage-targets dialog's
 * "ADJ. REVENUE (€M) · AS ENTERED BY FINANCE" box and the squad-cost-ratio Edit
 * dialog's adjusted-revenue box are the same figure, so they read and write
 * this variable rather than keeping a copy each. The claim is structural — the
 * two components import the same binding — instead of being a convention two
 * files agree to honour.
 *
 * It is kept OUT of `kpiTargetOverridesVar` on purpose. Adjusted revenue is an
 * INPUT to the squad-cost-ratio calculation, not a threshold the ratio is
 * judged against, and folding it into the Edit dialog's target payload would
 * make it look like one.
 *
 * ## What this variable is FOR now that a real mutation exists (SE-8101)
 *
 * `recordComplianceFinancialFigure` (SE-7593) is what makes the figure
 * durable — `adapters/adjustedRevenueSource.ts` wires both dialogs' saves to
 * it, effective-dated for real (the earlier revision of this doc claimed the
 * schema had no surface for that; it does). This variable now exists ONLY
 * for what SE-7878 built it for: keeping the two dialogs in sync with EACH
 * OTHER inside one session, without either one needing to know the other is
 * open. It is written ALONGSIDE a successful real save, never in place of
 * one, and a reload's truth comes from the mutation's own read, not from
 * this var — see `useComplianceOverview`'s `adjustedRevenue` field.
 *
 * Null rather than the frame's seeded number so "nobody has entered one" is
 * distinguishable from "somebody entered the default"; the seed comes from the
 * rule table when the draft is built.
 */
export const adjustedRevenueVar = makeVar<string | null>(null);

/**
 * Records the adjusted-revenue figure, from whichever surface entered it.
 * @param value - The figure as text, or null to clear it.
 * @returns Nothing.
 */
export const setAdjustedRevenue = (value: string | null): void => {
  adjustedRevenueVar(value);
};

/** A figure written here whose read-back has not caught up yet (SE-8453). */
export interface PendingAdjustedRevenue {
  /** The id of the figure the SERVER resolved as governing after the write. */
  readonly id: string;
  /** That figure as millions-scale dialog text. */
  readonly value: string;
}

/**
 * The figure this client just wrote, held only until the read can be shown to
 * have seen it (SE-8453).
 *
 * ## Why an id and not just a value
 *
 * `complianceFinancialFigures` is served by a read REPLICA, which can still be
 * behind the insert that just committed on the primary — so straight after a
 * save the read can answer with the figure the save replaced. Preferring the
 * saved value fixes that, but preferring it INDEFINITELY would also hide a
 * legitimate later change made by another user or another device, which is a
 * worse bug and a much quieter one.
 *
 * The id is what bounds it. `useAdjustedRevenueFigure` prefers this value only
 * while the rows coming back do NOT contain this id — the one condition under
 * which the read is DEMONSTRABLY behind rather than merely disagreeing. As soon
 * as the rows carry it, the read has caught up and wins again, whatever it now
 * says. Nothing clears this marker, and nothing needs to: it simply stops
 * applying, which is why there is no window in which a stale marker can outlive
 * its own reason for existing.
 */
export const pendingAdjustedRevenueVar = makeVar<PendingAdjustedRevenue | null>(
  null
);

/**
 * Records what this client just wrote, and which row the server said governs.
 * @param pending - The figure and its id, or null to forget it.
 * @returns Nothing.
 */
export const setPendingAdjustedRevenue = (
  pending: PendingAdjustedRevenue | null
): void => {
  pendingAdjustedRevenueVar(pending);
};

/**
 * Clears every targets-and-rules state. For tests and sign-out.
 *
 * Standalone rather than chained into `resetComplianceViewState`, following the
 * PSR store's precedent: each FC store resets itself, and chaining would also
 * make the page-level store depend on a modal surface it knows nothing about.
 * @returns Nothing.
 */
export const resetTargetsRulesState = (): void => {
  targetsRulesEpochVar(targetsRulesEpochVar() + 1);
  ruleThresholdOverridesVar({});
  ruleShowKpiOverridesVar({});
  adjustedRevenueVar(null);
  pendingAdjustedRevenueVar(null);
};

/**
 * How many times this store has been cleared (SE-8453).
 *
 * ## What this is for, and why clearing alone is not enough
 *
 * Clearing on a club switch stops state that is ALREADY here. It cannot stop
 * state still on its way: a write started under the previous club resolves
 * after the clear has run and puts that club's figure back into the store it
 * was just emptied from. The two are different halves of the same leak — one
 * is about what is sitting there, the other about what is still coming.
 *
 * So a caller with an asynchronous write reads this before starting and again
 * before recording the result; a change means the answer in its hands belongs
 * to a club nobody is looking at any more, and it must be dropped rather than
 * stored.
 *
 * Nothing renders from this: callers read it imperatively around an await, and
 * a direct call on a reactive var subscribes no component.
 * @returns The current epoch.
 */
export const readTargetsRulesEpoch = (): number => targetsRulesEpochVar();
